import { setProjectState } from '@starflow/app/store';
import { getCDNImage, getRemoteImageSize, getImageOssPath } from '@shakkerai/util-image';
import { requestModelVersionDetail } from '@shakkerai/services';
import { presetCheckpointsMap } from '@shakkerai/pro-models';
import { pickModelFields } from './model';
import { generateAndUploadMask } from '@shakkerai/pro-utils';
import { createUnit } from '../../../helpers/flow';
import { getVipState } from '@shakkerai/pro-store';

const fieldTypesMap = {
  INT: 'number',
  FLOAT: 'number',
  IMAGE: 'image',
  VIDEO: 'video',
  LIST: 'select',
  MODEL: 'checkpoint',
  STRING: 'textarea',
  BOOLEAN: 'switch',
  COLOR: 'color',
};

const dedupOptions = (options) => {
  // options: string[] || {value:string}[]

  if (!Array.isArray(options)) {
    return [];
  }

  const valueSet = new Set();

  return options.filter((option) => {
    const value = typeof option === 'string' ? option : option.value;

    if (valueSet.has(value)) {
      return false;
    }

    valueSet.add(value);
    return true;
  });
};

const convertFieldValue = async (field, isFake = false) => {
  if (field.type === 'lora' || field.type === 'checkpoint') {
    if (field.value?.presetId) {
      return presetCheckpointsMap[field.value?.presetId]?.versionDetail?.uuid;
    }

    return field.value?.uuid;
  }

  if (field.type === 'video') {
    return getImageOssPath(field.value?.videoUrl);
  }

  if (field.type === 'image') {
    return getImageOssPath(field.value?.url);
  }

  if (field.type === 'mask') {
    if (isFake) {
      // 如果isFake为true,代表在预估算力的时候调用，所有需要传图片的地方就随便传一个就可以，用于预估算力数
      return 'img/fake';
    }
    if (field.value?.mask && field.value?.sourceImage) {
      const { shapes } = field.value.mask;
      const { width, height, url } = field.value.sourceImage;
      if (shapes.length && width && height) {
        const maskUrl = await generateAndUploadMask({
          selections: shapes,
          crop: { x: 0, y: 0, width, height },
          toTempBucket: true,
          isCombine: true,
          url,
        });
        return getImageOssPath(maskUrl);
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  console.log(`[DEBUG]`, field.type, field.value);
  return field.value;
};

export const workflowGenerateParmasHandler = async ({ params, isTryClacMode = false }) => {
  const custom_prompt = {};

  for (const node of params.nodes) {
    const nodeData = { class_type: node.name, inputs: {} };

    for (const field of node.fields) {
      nodeData.inputs[`${field.name}`] = await convertFieldValue(field);
    }

    custom_prompt[node.id] = nodeData;
  }
  const taskQueuePriority = getVipState('vipAccelerate') ? 2 : 1; // 1 普通队列 2加速队列

  const projectData = {
    ...params,
    generateType: 'workflow',
    count: 1,
    width: 1024,
    height: 1024,
    renderWidth: 1024,
    renderHeight: 1024,
  };

  return {
    projectData,
    count: 1,
    width: 1024,
    height: 1024,
    taskQueuePriority,
    workflowParams: {
      mode: 1,
      projectData,
      taskQueuePriority: taskQueuePriority,
      generateType: 19,
      mode_type: 2,
      comfyUI_encap: {
        comfyUI_uuid: params.uuid,
        custom_prompt: custom_prompt,
      },
    },
  };
};

export const mapWorkflowParam = async (workflowData) => {
  const custom_prompt = {};

  for (const node of workflowData.nodes) {
    const nodeData = { class_type: node.name, inputs: {} };

    for (const field of node.fields) {
      const convertRes = await convertFieldValue(field, true);
      nodeData.inputs[`${field.name}`] = convertRes;
    }

    custom_prompt[node.id] = nodeData;
  }

  return {
    taskQueuePriority: 1,
    generateType: 19,
    mode_type: 2,
    comfyUI_encap: {
      comfyUI_uuid: workflowData.uuid,
      custom_prompt: custom_prompt,
    },
  };
};

export const initWorkflowData = async (rawData) => {
  const filledData = {
    uuid: rawData.uuid,
    modelUuid: rawData.modelUuid,
    versionId: rawData?.versionId,
    title: rawData.name || rawData.title,
    nodes: [],
    outputs: [],
  };

  const initialValues = Array.isArray(rawData.nodes)
    ? Object.fromEntries(
        rawData.nodes.map((node) => [
          node.id,
          Object.fromEntries(node.fields?.map((field) => [field.name, field.value])),
        ]),
      )
    : {};

  let workflowJsonUrl = rawData.attachment?.editModel;

  if (!workflowJsonUrl) {
    const baseDetail = await requestModelVersionDetail(rawData.uuid).then((res) => res.data);

    workflowJsonUrl = baseDetail.attachment.editModel || baseDetail.attachment.modelSource;

    if (!workflowJsonUrl) {
      console.error('attachment or editModel not exist');
      throw new Error('模型信息获取失败');
    }

    filledData.versionId = baseDetail?.versionId;
  }

  const workflowPromptData = await fetch(workflowJsonUrl).then((res) => res.json());
  filledData.outputs = workflowPromptData.outputs;

  for (const input of workflowPromptData.inputs) {
    const filledNode = {
      id: input.id,
      name: input.name,
      title: input.displayName || input.name,
      fields: [],
    };

    for (const param of input.params) {
      let type = fieldTypesMap[param.type] || param.type;

      if (type === 'textarea' && !param.multiline) {
        type = 'text';
      }

      if (type === 'checkpoint' && param.name?.match(/lora/i)) {
        type = 'lora';
      }

      // 这个不太准，不能这样来
      // if (param.name?.match(/video/i)) {
      //   type = 'video';
      // }
      var url;
      var size;
      if (type === 'image') {
        url = getCDNImage(param.defaultValue, true);
        size = await getRemoteImageSize(url);
      }
      if (type === 'image' && param.isMaskImage) {
        type = 'mask';
      }
      let urlObj;
      if (url) {
        if (type === 'mask') {
          urlObj = { sourceImage: { url, ...size } };
        } else if (type === 'image') {
          urlObj = { url, ...size };
        } else {
          urlObj = param.defaultValue;
        }
      } else {
        urlObj = param.defaultValue;
      }
      const filledField = {
        id: param.id,
        name: param.name,
        title: param.displayName || param.name,
        tooltip: param.tooltip,
        type,
        visible: param.defaultDisplay,
        value: urlObj,
      };

      if (type === 'select') {
        filledField.options = dedupOptions(filledField.options || param.enumList);
      }

      if (type === 'number') {
        filledField.min = param.min || 0;
        filledField.max = param.max || 100;
        filledField.step = param.step || 1;
      }

      if (initialValues[input.id]?.[param.name] !== undefined) {
        filledField.value = initialValues[input.id][param.name];
      } else if (type === 'lora' || type === 'checkpoint') {
        if (filledField.value) {
          try {
            const modelVersionDetail = await requestModelVersionDetail(filledField.value).then((res) => res.data);

            if (!modelVersionDetail?.uuid) {
              console.error('modelVersionDetail or uuid not exist');
              filledField.value = {
                uuid: param.defaultValue,
                error: '模型信息获取失败',
              };
            }
            filledField.value = pickModelFields(modelVersionDetail, true);
          } catch (e) {
            console.error('catch error', e);
            filledField.value = {
              uuid: param.defaultValue,
              error: '模型信息获取失败',
            };
          }
        }
      }

      filledNode.fields.push(filledField);
    }

    filledData.nodes.push(filledNode);
  }
  return filledData;
};

export const enterWorkflowMode = async (genInfo) => {
  setProjectState(
    {
      selection: [],
      currentElementTool: null,
    },
    {
      skipTracking: true,
    },
  );

  createUnit(
    {
      title: genInfo.title || genInfo.name,
      type: 'workflow',
      data: { initialized: false, rawData: genInfo },
    },
    { autoSelect: true },
  );
};
